import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth, canAccess, isAdmin } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import APAgeing from "@/pages/APAgeing";
import ARAgeing from "@/pages/ARAgeing";
import APLedger from "@/pages/Ledger";
import ARLedger from "@/pages/ARLedger";
import TDSSummary from "@/pages/TDSSummary";
import AdvanceRequests from "@/pages/AdvanceRequests";
import GSTR2 from "@/pages/GSTR2";
import GSTR3B from "@/pages/GSTR3B";
import RetentionTracker from "@/pages/RetentionTracker";
import ReportBuilder from "@/pages/ReportBuilder";
import ProjectPnL from "@/pages/ProjectPnL";
import Inventory from "@/pages/Inventory";
import BarcodePrinting from "@/pages/BarcodePrinting";
import StockAgeing from "@/pages/StockAgeing";
import SalesPerformance from "@/pages/SalesPerformance";
import ProformaInvoice from "@/pages/ProformaInvoice";
import CustomerProfile from "@/pages/CustomerProfile";
import VendorProfile from "@/pages/VendorProfile";
import PurchaseReco from "@/pages/PurchaseReco";
import TallyExport from "@/pages/TallyExport";
import GSTReturns from "@/pages/GSTReturns";
import SubconRecon from "@/pages/SubconRecon";
import Settings from "@/pages/Settings";
import UserManagement from "@/pages/UserManagement";

// Ordered page → route map, used to send a user to their first accessible page.
const PAGE_ROUTES = [
  ["dashboard", "/"],
  ["ap-ageing", "/ap-ageing"],
  ["ar-ageing", "/ar-ageing"],
  ["ap-ledger", "/ap-ledger"],
  ["ar-ledger", "/ar-ledger"],
  ["tds-summary", "/tds-summary"],
  ["advance-requests", "/advance-requests"],
  ["gstr2", "/gstr2"],
  ["gstr3b", "/gstr3b"],
  ["retention-tracker", "/retention-tracker"],
  ["project-pnl", "/project-pnl"],
  ["inventory", "/inventory"],
  ["report-builder", "/report-builder"],
];

function firstAllowedPath(user) {
  if (isAdmin(user)) return "/";
  const hit = PAGE_ROUTES.find(([key]) => canAccess(user, key));
  return hit ? hit[1] : null;
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-sky-700 border-t-transparent" />
      </div>
    );
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function NoAccess() {
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6" data-testid="no-access-screen">
      <h1 className="text-2xl font-extrabold text-slate-900 mb-2">No reports assigned yet</h1>
      <p className="text-sm text-slate-500 max-w-md">Your account is active, but an administrator hasn't granted you access to any reports yet. Please contact your admin to get access.</p>
    </div>
  );
}

function RequireAccess({ page, children }) {
  const { user } = useAuth();
  if (canAccess(user, page)) return children;
  const dest = firstAllowedPath(user);
  if (dest) return <Navigate to={dest} replace />;
  return <NoAccess />;
}

function RequireAdmin({ children }) {
  const { user } = useAuth();
  if (isAdmin(user)) return children;
  const dest = firstAllowedPath(user);
  return dest ? <Navigate to={dest} replace /> : <NoAccess />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route path="/" element={<RequireAccess page="dashboard"><Dashboard /></RequireAccess>} />
              <Route path="/ap-ageing" element={<RequireAccess page="ap-ageing"><APAgeing /></RequireAccess>} />
              <Route path="/ar-ageing" element={<RequireAccess page="ar-ageing"><ARAgeing /></RequireAccess>} />
              <Route path="/ap-ledger" element={<RequireAccess page="ap-ledger"><APLedger /></RequireAccess>} />
              <Route path="/ar-ledger" element={<RequireAccess page="ar-ledger"><ARLedger /></RequireAccess>} />
              <Route path="/tds-summary" element={<RequireAccess page="tds-summary"><TDSSummary /></RequireAccess>} />
              <Route path="/advance-requests" element={<RequireAccess page="advance-requests"><AdvanceRequests /></RequireAccess>} />
              <Route path="/gstr2" element={<RequireAccess page="gstr2"><GSTR2 /></RequireAccess>} />
              <Route path="/gstr3b" element={<RequireAccess page="gstr3b"><GSTR3B /></RequireAccess>} />
              <Route path="/retention-tracker" element={<RequireAccess page="retention-tracker"><RetentionTracker /></RequireAccess>} />
              <Route path="/project-pnl" element={<RequireAccess page="project-pnl"><ProjectPnL /></RequireAccess>} />
              <Route path="/inventory" element={<RequireAccess page="inventory"><Inventory /></RequireAccess>} />
              <Route path="/barcode-printing" element={<RequireAccess page="barcode-printing"><BarcodePrinting /></RequireAccess>} />
              <Route path="/stock-ageing" element={<RequireAccess page="stock-ageing"><StockAgeing /></RequireAccess>} />
              <Route path="/purchase-reco" element={<RequireAccess page="purchase-reco"><PurchaseReco /></RequireAccess>} />
              <Route path="/report-builder" element={<RequireAccess page="report-builder"><ReportBuilder /></RequireAccess>} />
              <Route path="/vendor/:code" element={<RequireAccess page="ap-ageing"><VendorProfile /></RequireAccess>} />
              <Route path="/sales/customer/:code" element={<RequireAccess page="sales-performance"><CustomerProfile /></RequireAccess>} />
              <Route path="/sales/proforma" element={<RequireAccess page="proforma-invoice"><ProformaInvoice /></RequireAccess>} />
              <Route path="/sales/:section" element={<RequireAccess page="sales-performance"><SalesPerformance /></RequireAccess>} />
              <Route path="/users" element={<RequireAdmin><UserManagement /></RequireAdmin>} />
              <Route path="/tally-export" element={<RequireAccess page="tally-export"><TallyExport /></RequireAccess>} />
              <Route path="/gst-returns" element={<RequireAccess page="gst-returns"><GSTReturns /></RequireAccess>} />
              <Route path="/subcon-recon" element={<RequireAccess page="subcon-recon"><SubconRecon /></RequireAccess>} />
              <Route path="/settings" element={<RequireAdmin><Settings /></RequireAdmin>} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
